import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, Loader2, Minus, Plus, Coins } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { BOOK_TIERS, BASE_BOOK_PRICE, MAX_BOOKS, bookQuote, PACKAGE_INCLUDES } from "@/lib/pricing";
import { toast } from "sonner";

const TierTable = ({ qty }) => (
  <div className="grid grid-cols-7 gap-1 text-center" data-testid="pricing-tier-table">
    {BOOK_TIERS.map((t, i) => {
      const next = BOOK_TIERS[i + 1];
      const active = qty >= t.min && (!next || qty < next.min);
      return (
        <div key={t.min} className={`rounded-md py-1.5 px-1 border transition-colors ${active ? "border-gold-400 bg-gold-400/10" : "border-gold-500/10"}`}>
          <div className="font-ui text-[10px] text-slate-500">{next ? `${t.min}–${next.min - 1}` : `${t.min}+`}</div>
          <div className={`font-ui text-sm font-semibold ${active ? "text-gold-300" : "text-slate-300"}`}>${t.unit_price}</div>
        </div>
      );
    })}
  </div>
);

export const BookCreditCheckout = ({ initialQty = 1, onNeedAuth, bookId = "" }) => {
  const [qty, setQty] = useState(initialQty);
  const [draft, setDraft] = useState(null);  // raw text while the field is being typed in
  const [loading, setLoading] = useState(false);
  const q = bookQuote(qty);
  const set = (n) => { setDraft(null); setQty(Math.max(1, Math.min(MAX_BOOKS, Math.floor(Number(n)) || 1))); };
  const type = (v) => {
    const digits = v.replace(/\D/g, "").slice(0, 3);
    setDraft(digits);
    if (digits) setQty(Math.max(1, Math.min(MAX_BOOKS, parseInt(digits, 10))));
  };
  const buy = async () => {
    if (onNeedAuth) return onNeedAuth();
    setLoading(true);
    try {
      const { checkout_url } = await api.checkout("book_credits", { quantity: qty, book_id: bookId });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(apiError(e, "Could not start checkout."));
      setLoading(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="book-credit-checkout">
      <div className="flex items-center gap-3">
        <button data-testid="pricing-qty-minus" onClick={() => set(qty - 1)} className="w-9 h-9 rounded-lg border border-gold-500/20 flex items-center justify-center hover:border-gold-400/60 transition-colors"><Minus className="w-4 h-4" /></button>
        <input data-testid="pricing-qty-input" type="text" inputMode="numeric" value={draft ?? qty} onChange={(e) => type(e.target.value)}
          onBlur={() => set(qty)}
          className="w-16 h-9 text-center rounded-lg bg-navy-900 border border-gold-500/20 font-ui text-lg text-slate-100" />
        <button data-testid="pricing-qty-plus" onClick={() => set(qty + 1)} className="w-9 h-9 rounded-lg border border-gold-500/20 flex items-center justify-center hover:border-gold-400/60 transition-colors"><Plus className="w-4 h-4" /></button>
        <span className="font-ui text-sm text-slate-400">book{qty > 1 ? "s" : ""}</span>
      </div>
      <TierTable qty={qty} />
      <div className="rounded-xl border border-gold-500/15 bg-navy-900/50 p-4 grid grid-cols-3 gap-3">
        <div><p className="font-ui text-[11px] uppercase tracking-wider text-slate-500">Per book</p><p data-testid="pricing-unit-price" className="font-display text-2xl text-slate-100">${q.unit_price}</p></div>
        <div><p className="font-ui text-[11px] uppercase tracking-wider text-slate-500">Total</p><p data-testid="pricing-total" className="font-display text-2xl text-gold-300">${q.total}</p></div>
        <div><p className="font-ui text-[11px] uppercase tracking-wider text-slate-500">You save</p><p data-testid="pricing-savings" className="font-display text-2xl text-emerald-300">${q.savings}</p></div>
      </div>
      <p className="font-ui text-xs text-slate-500">Every book in the order gets the {qty >= 3 ? "same discounted" : "standard"} price (base ${BASE_BOOK_PRICE}). Credits never expire.</p>
      <p data-testid="pricing-tax-note" className="font-ui text-xs text-slate-400">Prices are in USD before tax. Any sales tax or VAT is worked out by Stripe from your billing address, and you'll see the final total before you pay.</p>
      <button data-testid="pricing-buy-books" onClick={buy} disabled={loading}
        className="w-full flex items-center justify-center gap-2 font-ui font-semibold bg-gold-400 hover:bg-gold-300 text-stone-950 rounded-xl py-3 transition-colors disabled:opacity-60">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />} Buy {qty} book credit{qty > 1 ? "s" : ""} · ${q.total}
      </button>
    </div>
  );
};

export const PricingModal = ({ open, onOpenChange, reason, bookId }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="pricing-modal" className="max-w-2xl bg-navy-950 border-gold-500/12 text-slate-100 max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display text-3xl text-slate-100">Buy book credits</DialogTitle>
        <DialogDescription className="font-read text-sm text-slate-400">One credit = one complete book with its Amazon KDP publishing package. No subscriptions, no add-ons.</DialogDescription>
        {reason && <p className="font-read text-sm text-slate-300/90 mt-1">{reason}</p>}
      </DialogHeader>
      <BookCreditCheckout bookId={bookId} />
      <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 pt-2" data-testid="pricing-includes">
        {PACKAGE_INCLUDES.map((x) => (
          <li key={x} className="flex items-start gap-2 font-ui text-sm text-slate-300"><Check className="w-4 h-4 text-gold-300 mt-0.5 shrink-0" />{x}</li>
        ))}
      </ul>
    </DialogContent>
  </Dialog>
);
